import PinTileGrid from '~/modules/design-board/component/all-pins/PinTileGrid';
import BoardDetailFilter from '~/modules/design-board/component/board-detail/BoardDetailFilter';
import type { DesignBoardWithDetailedPinsFragment } from 'generated/graphql';
import { DesignBoardPinTargetType } from 'generated/graphql';
import type { PinTypeCountData } from '~/modules/design-board/type';
import type { PaginationOptions } from '~/modules/root/util/get-pagination-opts.server';
import NoPinWithUpload from '~/modules/design-board/component/board-detail/NoPinWithUpload';
import MainContentLayout from '~/modules/design-board/component/page-layout/MainContentLayout';
import BoardDescription from '~/modules/design-board/component/board-detail/BoardDescription';
import React, { useEffect, useRef, useState } from 'react';
import PinActionBar from './PinActionBar';
import BottomPagination from '../pagination/BottomPagination';
import {
    pinsWithCheckedPins,
    updateAllBoards,
    updateCheck,
} from '~/modules/design-board/component/pin-select-bar/PinSelectBar';
import { useDesignBoardPageState } from '../context/DesignBoardContext';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import * as S from './BoardDetail.style';
import type { DetailedPinWithChecked } from '~/modules/design-board/component/all-pins/AllPins';
import { useDeletePin, useUrlPinTargetType } from '~/modules/design-board/hook';
import { useLocation, useNavigate } from '@remix-run/react';
import LeftSideNav from '../left-side-nav/LeftSideNav';
import { DEFAULT_SIDE_NAV_ITEM } from '~/modules/design-board/config';
import { getSideNavLinksByPins } from '~/modules/design-board/util';
import { LeftSideNavFilterWrapper } from '../left-side-nav/LeftSideNav.style';
import type { DesignBoardsPinsLoaderData } from '~/routes/_app.member.design-boards.pins';

interface BoardDetailProps {
    allPinsData: DesignBoardsPinsLoaderData['allPinsData'];
    boardDetailData: DesignBoardWithDetailedPinsFragment;
    pagination: PaginationOptions;
    targetType: DesignBoardPinTargetType;
    pinCounts: PinTypeCountData;
}

const BoardDetail = ({
    allPinsData,
    boardDetailData,
    pagination,
    targetType,
    pinCounts,
}: BoardDetailProps) => {
    const { mobile } = useAppDisplayModeContext();
    const location = useLocation();
    const navigate = useNavigate();
    const isAllowOrganise = mobile ? false : true; // todo: change it when implement the organise feature

    const { css } = useStyles();
    const urlPinType = useUrlPinTargetType();

    const [pinListWithChecked, setPinListWithChecked] = useState(
        pinsWithCheckedPins(allPinsData)
    );
    const [pinCountAfterUpload, setPinCountAfterUpload] = useState(
        boardDetailData.PinsCount
    );

    const filesFromLocation = location.state?.uploadResult;
    const uploadedFromLocationRef = useRef(false);

    const {
        dispatch,
        state: { uploadFiles },
    } = useDesignBoardPageState();

    const { deletePin, deletePinDialog } = useDeletePin();

    useEffect(() => {
        setPinListWithChecked(pinsWithCheckedPins(allPinsData));
    }, [allPinsData, setPinListWithChecked]);

    useEffect(() => {
        dispatch({
            type: 'SetEditMode',
            canEdit: boardDetailData.CanEdit,
        });

        dispatch({
            type: 'SetCommentPermission',
            canComment: boardDetailData.Permission.CanAddComments,
        });
    }, [
        boardDetailData.CanEdit,
        boardDetailData.Permission.CanAddComments,
        dispatch,
    ]);

    useEffect(() => {
        setPinCountAfterUpload(pagination.totalCount);
    }, [pagination.totalCount]);

    useEffect(() => {
        if (
            uploadFiles.length === 0 &&
            !uploadedFromLocationRef.current &&
            filesFromLocation
        ) {
            dispatch({
                type: 'UploadPin',
                uploadFiles: filesFromLocation,
            });
            uploadedFromLocationRef.current = true;
        }
    }, [uploadFiles.length, filesFromLocation, dispatch]);

    const onUploadComplete = (uploadedPins: DetailedPinWithChecked[]) => {
        const newPins = mobile
            ? pinListWithChecked.filter(
                  (pin) =>
                      pin.TargetFilterType ===
                      DesignBoardPinTargetType.MemberUpload
              )
            : pinListWithChecked;

        setPinListWithChecked([...uploadedPins, ...newPins]);
        setPinCountAfterUpload(uploadedPins.length + pinCountAfterUpload);

        // clear the location.state to avoid hard refresh issue
        setTimeout(() => {
            navigate(location.pathname + location.search, { replace: true });
        }, 1000);
    };

    return (
        <MainContentLayout
            topComponent={
                <BoardDetailFilter
                    board={boardDetailData}
                    pinCountAfterUpload={pinCountAfterUpload}
                    backItem={DEFAULT_SIDE_NAV_ITEM}
                />
            }
            leftSideNav={
                <div className={css(LeftSideNavFilterWrapper)}>
                    <LeftSideNav
                        topItem={DEFAULT_SIDE_NAV_ITEM}
                        navItems={getSideNavLinksByPins(pinCounts)}
                        targetType={targetType}
                    />
                </div>
            }
        >
            <BoardDescription board={boardDetailData} />

            {isAllowOrganise && boardDetailData.PinsCount > 0 && (
                <PinActionBar
                    pins={pinListWithChecked}
                    boardId={boardDetailData.ID}
                    onDelete={() => {
                        deletePin(
                            pinListWithChecked.reduce((pinString, pin) => {
                                if (!pin.checked) return pinString;
                                if (pinString === '') return `${pin.ID}`;
                                return `${pinString}, ${pin.ID}`;
                            }, '')
                        );
                    }}
                    onChange={(checked) =>
                        updateAllBoards({
                            checked,
                            pinListWithChecked,
                            setPinListWithChecked,
                        })
                    }
                />
            )}

            {pinListWithChecked.length > 0 || uploadFiles.length > 0 ? (
                <PinTileGrid
                    pins={pinListWithChecked}
                    onUpdate={(boardId, checked) =>
                        updateCheck({
                            boardId,
                            checked,
                            pinListWithChecked,
                            setPinListWithChecked,
                        })
                    }
                    targetType={urlPinType || targetType}
                    onDelete={(pinId) => {
                        deletePin(pinId);
                    }}
                    boardId={boardDetailData.ID}
                    onUploadComplete={onUploadComplete}
                />
            ) : (
                <NoPinWithUpload
                    canUploadFile={boardDetailData.Permission.CanUploadFile}
                />
            )}

            {deletePinDialog(css(S.DeleteDialog))}

            <BottomPagination
                pagination={pagination}
                itemName="Pin"
                pinCountAfterUpload={pinCountAfterUpload}
            />
        </MainContentLayout>
    );
};

export default BoardDetail;
